import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MRT_ColumnFiltersState } from 'material-react-table';
import { PaginationOptions, TableState } from '../userOrg/injectedUserOrgApi';

const persistTableOptions = (state: TableOptionsExtended) => {
    localStorage.setItem('tableOptions', JSON.stringify(state));
};

type TableStateExtended = TableState & {
    searchError?: string;
    selectedRowIds?: any[];
    columnSearch?: any;
};

type TableOptionsExtended = {
    projects: TableState;
    customHPCScriptWorkflows: TableState;
    workflows: TableState;
    uploadDatasets: TableStateExtended;
    workflowDatasets: TableStateExtended;
    publicDatasets: TableStateExtended;
    jobscripts: TableStateExtended;
    jobscriptVersions: TableStateExtended;
    locations: TableState;
    providers: TableState;
    resourceManagement: TableState;
    uploadTransferStatuses: TableState;
    stageTransferStatuses: TableState;
    deleteTransferStatuses: TableState;
};

const getInitialTableOptions = (): TableOptionsExtended => {
    try {
        const storedOptions = localStorage.getItem('tableOptions');
        if (storedOptions) {
            const parsed = JSON.parse(storedOptions);
            return {
                projects: parsed.projects ?? { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
                customHPCScriptWorkflows: parsed.customHPCScriptWorkflows ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 5 },
                },
                workflows: parsed.workflows ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                uploadDatasets: parsed.uploadDatasets ?? {
                    columnSearch: undefined,
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                    selectedRowIds: [],
                    searchError: undefined,
                },
                workflowDatasets: parsed.workflowDatasets ?? {
                    columnSearch: undefined,
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                    selectedRowIds: [],
                    searchError: undefined,
                },
                publicDatasets: parsed.publicDatasets ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                    selectedRowIds: [],
                    searchError: undefined,
                },
                jobscripts: parsed.jobscripts ?? {
                    columnSearch: undefined,
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 5 },
                    selectedRowIds: [],
                    searchError: undefined,
                },
                jobscriptVersions: parsed.jobscriptVersions ?? {
                    columnSearch: undefined,
                    pagination: { pageIndex: 0, pageSize: 5 },
                },
                locations: parsed.locations ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                providers: parsed.providers ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                resourceManagement: parsed.resourceManagement ?? {
                    searchText: '',
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                uploadTransferStatuses: parsed.uploadTransferStatuses ?? {
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                stageTransferStatuses: parsed.stageTransferStatuses ?? {
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
                deleteTransferStatuses: parsed.deleteTransferStatuses ?? {
                    pagination: { pageIndex: 0, pageSize: 10 },
                },
            };
        }
    } catch (error) {
        console.error('Error parsing tableOptions from localStorage', error);
    }
    return {
        projects: { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
        customHPCScriptWorkflows: { searchText: '', pagination: { pageIndex: 0, pageSize: 5 } },
        workflows: { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
        uploadDatasets: {
            columnSearch: undefined,
            searchText: '',
            pagination: { pageIndex: 0, pageSize: 10 },
            selectedRowIds: [],
            searchError: undefined,
        },
        workflowDatasets: {
            columnSearch: undefined,
            searchText: '',
            pagination: { pageIndex: 0, pageSize: 10 },
            selectedRowIds: [],
            searchError: undefined,
        },
        publicDatasets: {
            searchText: '',
            pagination: { pageIndex: 0, pageSize: 10 },
            selectedRowIds: [],
            searchError: undefined,
        },
        jobscripts: {
            columnSearch: undefined,
            searchText: '',
            pagination: { pageIndex: 0, pageSize: 5 },
            selectedRowIds: [],
            searchError: undefined,
        },
        jobscriptVersions: { columnSearch: undefined, pagination: { pageIndex: 0, pageSize: 5 } },
        locations: { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
        providers: { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
        resourceManagement: { searchText: '', pagination: { pageIndex: 0, pageSize: 10 } },
        uploadTransferStatuses: { pagination: { pageIndex: 0, pageSize: 10 } },
        stageTransferStatuses: { pagination: { pageIndex: 0, pageSize: 10 } },
        deleteTransferStatuses: { pagination: { pageIndex: 0, pageSize: 10 } },
    };
};

const initialTableOptions = getInitialTableOptions();

const initialState: TableOptionsExtended = {
    projects: {
        searchText: initialTableOptions.projects?.searchText,
        pagination: initialTableOptions.projects?.pagination ?? { pageIndex: 0, pageSize: 10 },
    },
    customHPCScriptWorkflows: {
        searchText: initialTableOptions.customHPCScriptWorkflows?.searchText,
        pagination: initialTableOptions.customHPCScriptWorkflows?.pagination ?? {
            pageIndex: 0,
            pageSize: 5,
        },
    },
    workflows: {
        searchText: initialTableOptions.workflows?.searchText,
        pagination: initialTableOptions.workflows?.pagination ?? { pageIndex: 0, pageSize: 10 },
    },
    uploadDatasets: {
        columnSearch: initialTableOptions.uploadDatasets?.columnSearch,
        searchText: initialTableOptions.uploadDatasets?.searchText,
        pagination: initialTableOptions.uploadDatasets?.pagination ?? { pageIndex: 0, pageSize: 10 },
        selectedRowIds: [],
        searchError:
            initialTableOptions.uploadDatasets && 'searchError' in initialTableOptions.uploadDatasets
                ? initialTableOptions.uploadDatasets.searchError
                : undefined,
    },
    workflowDatasets: {
        columnSearch: initialTableOptions.workflowDatasets?.columnSearch,
        searchText: initialTableOptions.workflowDatasets?.searchText,
        pagination: initialTableOptions.workflowDatasets?.pagination ?? { pageIndex: 0, pageSize: 10 },
        selectedRowIds: [],
        searchError:
            initialTableOptions.workflowDatasets && 'searchError' in initialTableOptions.workflowDatasets
                ? initialTableOptions.workflowDatasets.searchError
                : undefined,
    },
    publicDatasets: {
        searchText: initialTableOptions.publicDatasets?.searchText,
        pagination: initialTableOptions.publicDatasets?.pagination ?? { pageIndex: 0, pageSize: 10 },
        selectedRowIds: [],
        searchError:
            initialTableOptions.publicDatasets && 'searchError' in initialTableOptions.publicDatasets
                ? initialTableOptions.publicDatasets.searchError
                : undefined,
    },
    jobscripts: {
        columnSearch: initialTableOptions.jobscripts?.columnSearch,
        searchText: initialTableOptions.jobscripts?.searchText,
        pagination: initialTableOptions.jobscripts?.pagination ?? { pageIndex: 0, pageSize: 5 },
        selectedRowIds: [],
        searchError:
            initialTableOptions.jobscripts && 'searchError' in initialTableOptions.jobscripts
                ? initialTableOptions.jobscripts.searchError
                : undefined,
    },
    jobscriptVersions: {
        columnSearch: initialTableOptions.jobscriptVersions?.columnSearch,
        pagination: initialTableOptions.jobscriptVersions?.pagination ?? { pageIndex: 0, pageSize: 5 },
    },
    locations: {
        searchText: initialTableOptions.locations?.searchText,
        pagination: initialTableOptions.locations?.pagination ?? { pageIndex: 0, pageSize: 10 },
    },
    providers: {
        searchText: initialTableOptions.providers?.searchText,
        pagination: initialTableOptions.providers?.pagination ?? { pageIndex: 0, pageSize: 10 },
    },
    resourceManagement: {
        searchText: initialTableOptions.resourceManagement?.searchText,
        pagination: initialTableOptions.resourceManagement?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
        },
    },
    uploadTransferStatuses: {
        pagination: initialTableOptions.uploadTransferStatuses?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
        },
    },
    stageTransferStatuses: {
        pagination: initialTableOptions.stageTransferStatuses?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
        },
    },
    deleteTransferStatuses: {
        pagination: initialTableOptions.deleteTransferStatuses?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
        },
    },
};

const tableOptionsSlice = createSlice({
    name: 'tableOptions',
    initialState,
    reducers: {
        setProjectsSearchText: (state, action: PayloadAction<string>) => {
            state.projects.searchText = action.payload;
            persistTableOptions(state);
        },
        setProjectsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.projects.pagination = action.payload;
            persistTableOptions(state);
        },
        setWorkflowsSearchText: (state, action: PayloadAction<string>) => {
            state.workflows.searchText = action.payload;
            persistTableOptions(state);
        },
        setWorkflowsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.workflows.pagination = action.payload;
            persistTableOptions(state);
        },
        setUploadDatasetsSelectedRowIds: (state, action: PayloadAction<string[]>) => {
            state.uploadDatasets.selectedRowIds = action.payload;
            persistTableOptions(state);
        },
        resetUploadDatasetsSelectedRowIds: state => {
            state.uploadDatasets.selectedRowIds = [];
            persistTableOptions(state);
        },
        setUploadDatasetsColumnSearch: (state, action: PayloadAction<MRT_ColumnFiltersState | undefined>) => {
            state.uploadDatasets.columnSearch = action.payload;
            persistTableOptions(state);
        },
        setUploadDatasetsSearchError: (state, action: PayloadAction<string | undefined>) => {
            state.uploadDatasets.searchError = action.payload;
            persistTableOptions(state);
        },
        setWorkflowDatasetsSearchError: (state, action: PayloadAction<string | undefined>) => {
            state.workflowDatasets.searchError = action.payload;
            persistTableOptions(state);
        },
        setPublicDatasetsSearchError: (state, action: PayloadAction<string | undefined>) => {
            state.publicDatasets.searchError = action.payload;
            persistTableOptions(state);
        },
        setJobscriptsSearchError: (state, action: PayloadAction<string | undefined>) => {
            state.jobscripts.searchError = action.payload;
            persistTableOptions(state);
        },
        setWorkflowDatasetsColumnSearch: (state, action: PayloadAction<MRT_ColumnFiltersState | undefined>) => {
            state.workflowDatasets.columnSearch = action.payload;
            persistTableOptions(state);
        },
        setJobscriptsColumnSearch: (state, action: PayloadAction<MRT_ColumnFiltersState | undefined>) => {
            state.jobscripts.columnSearch = action.payload;
            persistTableOptions(state);
        },
        setWorkflowDatasetsSelectedRowIds: (state, action: PayloadAction<any[]>) => {
            state.workflowDatasets.selectedRowIds = action.payload;
            persistTableOptions(state);
        },
        setPublicDatasetsSelectedRowIds: (state, action: PayloadAction<any[]>) => {
            state.publicDatasets.selectedRowIds = action.payload;
            persistTableOptions(state);
        },
        resetPublicDatasetsSelectedRowIds: state => {
            state.publicDatasets.selectedRowIds = [];
            persistTableOptions(state);
        },
        setJobscriptsSelectedRowIds: (state, action: PayloadAction<any[]>) => {
            state.jobscripts.selectedRowIds = action.payload;
            persistTableOptions(state);
        },
        setUploadDatasetsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.uploadDatasets.pagination = action.payload;
            persistTableOptions(state);
        },
        setJobscriptsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.jobscripts.pagination = action.payload;
            persistTableOptions(state);
        },
        setJobscriptVersionsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.jobscriptVersions.pagination = action.payload;
            persistTableOptions(state);
        },
        setWorkflowDatasetsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.workflowDatasets.pagination = action.payload;
            persistTableOptions(state);
        },
        setWorkflowDatasetsSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.workflowDatasets.searchText = action.payload;
            persistTableOptions(state);
        },
        setPublicDatasetsSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.publicDatasets.searchText = action.payload;
            persistTableOptions(state);
        },
        setUploadDatasetsSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.uploadDatasets.searchText = action.payload;
            persistTableOptions(state);
        },
        setJobscriptsSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.jobscripts.searchText = action.payload;
            persistTableOptions(state);
        },
        setJobscriptVersionsColumnSearch: (state, action: PayloadAction<MRT_ColumnFiltersState | undefined>) => {
            state.jobscriptVersions.columnSearch = action.payload;
            persistTableOptions(state);
        },
        setPublicDatasetsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.publicDatasets.pagination = action.payload;
            persistTableOptions(state);
        },
        setLocationsSearchText: (state, action: PayloadAction<string>) => {
            state.locations.searchText = action.payload;
            persistTableOptions(state);
        },
        setLocationsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.locations.pagination = action.payload;
            persistTableOptions(state);
        },
        setProvidersSearchText: (state, action: PayloadAction<string>) => {
            state.providers.searchText = action.payload;
            persistTableOptions(state);
        },
        setProvidersPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.providers.pagination = action.payload;
            persistTableOptions(state);
        },
        setResourceManagementSearchText: (state, action: PayloadAction<string>) => {
            state.resourceManagement.searchText = action.payload;
            persistTableOptions(state);
        },
        setResourceManagementPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.resourceManagement.pagination = action.payload;
            persistTableOptions(state);
        },
        setUploadTransferStatusesPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.uploadTransferStatuses.pagination = action.payload;
            persistTableOptions(state);
        },
        setStageTransferStatusesPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.stageTransferStatuses.pagination = action.payload;
            persistTableOptions(state);
        },
        setDeleteTransferStatusesPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.deleteTransferStatuses.pagination = action.payload;
            persistTableOptions(state);
        },
        setcustomHPCScriptWorkflowsSearchText: (state, action: PayloadAction<string>) => {
            state.customHPCScriptWorkflows.searchText = action.payload;
            persistTableOptions(state);
        },
        setcustomHPCScriptWorkflowsPagination: (state, action: PayloadAction<PaginationOptions>) => {
            state.customHPCScriptWorkflows.pagination = action.payload;
            persistTableOptions(state);
        },
    },
});

export const {
    setProjectsSearchText,
    setProjectsPagination,
    setUploadDatasetsPagination,
    setWorkflowsSearchText,
    setLocationsSearchText,
    setResourceManagementSearchText,
    setLocationsPagination,
    setProvidersSearchText,
    setProvidersPagination,
    setPublicDatasetsPagination,
    setResourceManagementPagination,
    setUploadTransferStatusesPagination,
    setWorkflowDatasetsPagination,
    setWorkflowsPagination,
    setUploadDatasetsSelectedRowIds,
    setPublicDatasetsSelectedRowIds,
    setWorkflowDatasetsSelectedRowIds,
    setJobscriptsSelectedRowIds,
    setJobscriptsPagination,
    setcustomHPCScriptWorkflowsPagination,
    setcustomHPCScriptWorkflowsSearchText,
    setJobscriptVersionsColumnSearch,
    setJobscriptVersionsPagination,
    setJobscriptsSearchText,
    setUploadDatasetsSearchText,
    setPublicDatasetsSearchText,
    setWorkflowDatasetsSearchText,
    setUploadDatasetsColumnSearch,
    setWorkflowDatasetsColumnSearch,
    setJobscriptsColumnSearch,
    setStageTransferStatusesPagination,
    setDeleteTransferStatusesPagination,
    setUploadDatasetsSearchError,
    setWorkflowDatasetsSearchError,
    setPublicDatasetsSearchError,
    setJobscriptsSearchError,
    resetPublicDatasetsSelectedRowIds,
    resetUploadDatasetsSelectedRowIds,
} = tableOptionsSlice.actions;

export default tableOptionsSlice.reducer;
